import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Eye,
  Send,
  Bell,
  BellOff,
  ArrowRight,
  Info,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { EVENTO_WIZARD, EVENTO_PUB } from "@/constants/testIds";
import EstadoEventoBadge from "@/components/common/EstadoEventoBadge";
import { esUrlImagenSegura, fallbackImagenEvento, sanearUrlImagen } from "@/utils/imagenes";
import {
  ESTADO_EVENTO,
  estadoDespuesDePublicar,
  formatearFechaCorta,
  agruparMesasPorEstado,
  salonesUtilizados,
  adaptarEventoParaSocio,
  checklistPublicacion,
  publicarEvento,
} from "@/data/adminEventosStore";
import { getAdminSession } from "@/data/adminAuthStore";
import {
  listarSalonesAcceso,
  ordenarSalones,
  resumenSalonesAcceso,
} from "@/data/salonesAccesoStore";
import EventoDetalleView from "@/screens/app/EventoDetalleView";

const fmtDinero = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "$ —";
  return num.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });
};

export default function PasoPublicacion({ valores, onGoToStep, onEventoActualizado }) {
  const [openPreview, setOpenPreview] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [notificar, setNotificar] = useState(
    valores.notificarAlPublicar ? "si" : "no",
  );

  const esAG = (valores.modalidad || "MESAS") === "ACCESO_GENERAL";

  // Acceso General · asegura que los salones estén cargados para checklist y
  // resumen aunque el admin salte directo al Paso 5 o recargue en edición.
  useEffect(() => {
    let cancelado = false;
    if (esAG && valores.id) {
      listarSalonesAcceso(valores.id).then((lista) => {
        if (cancelado) return;
        const ord = ordenarSalones(lista);
        onEventoActualizado && onEventoActualizado({ salonesAcceso: ord });
      });
    }
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAG, valores.id]);

  const check = useMemo(() => checklistPublicacion(valores), [valores]);
  const desglose = useMemo(
    () => agruparMesasPorEstado(valores.mesas || []),
    [valores.mesas],
  );
  const salones = useMemo(() => salonesUtilizados(valores), [valores]);
  const salonesAG = useMemo(
    () => ordenarSalones(valores.salonesAcceso || []),
    [valores.salonesAcceso],
  );
  const resumenAG = useMemo(() => resumenSalonesAcceso(salonesAG), [salonesAG]);

  const yaPublicado =
    valores.estado &&
    valores.estado !== ESTADO_EVENTO.BORRADOR &&
    valores.estado !== ESTADO_EVENTO.CANCELADO;

  const gruposOrden = [
    "Información general",
    "Configuración",
    "Plano y mesas",
    "Salones y boletos",
    "Configuración comercial",
    "Validaciones cruzadas",
  ];
  const gruposMap = check.checks.reduce((acc, c) => {
    (acc[c.grupo] = acc[c.grupo] || []).push(c);
    return acc;
  }, {});

  const publicar = async () => {
    const sesion = getAdminSession();
    const res = await publicarEvento({
      id: valores.id,
      sesion,
      notificar: notificar === "si",
    });
    if (!res || res.error) {
      toast.error("No se pudo publicar. Revisa el checklist.");
      return;
    }
    onEventoActualizado && onEventoActualizado(res);
    toast.success(
      res.estado === ESTADO_EVENTO.VENTA_ACTIVA
        ? "Evento publicado · Venta activa."
        : "Evento publicado · Aparecerá al socio.",
    );
    if (notificar === "si") {
      toast.info(
        "Preferencia registrada: se enviará cuando el servicio de notificaciones esté disponible.",
      );
    }
    setOpenConfirm(false);
  };

  const estadoObjetivo = estadoDespuesDePublicar(valores);

  return (
    <div
      data-testid={EVENTO_WIZARD.stepContent(5)}
      className="flex flex-col gap-6"
    >
      <header>
        <span className="overline text-accent">Paso 5</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Revisión y publicación
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Verifica que todo esté correcto antes de exponer el evento al socio.
        </p>
      </header>

      {yaPublicado && (
        <div className="rounded-2xl border hairline bg-primary/5 dark:bg-accent/10 px-5 py-4 flex items-center gap-3 text-sm">
          <Info className="w-4 h-4 text-accent" strokeWidth={1.7} />
          <span>
            Este evento ya fue publicado el{" "}
            <b>
              {valores.fechaPublicacion
                ? new Date(valores.fechaPublicacion).toLocaleString("es-MX")
                : "—"}
            </b>{" "}
            por <b>{valores.publicadoPor || "—"}</b>.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Checklist */}
        <section
          data-testid={EVENTO_PUB.checklist}
          className="lg:col-span-2 rounded-2xl border hairline bg-card p-6 flex flex-col gap-5"
        >
          <header className="flex items-center gap-3">
            <div
              className={cn(
                "w-10 h-10 rounded-xl grid place-items-center ring-1",
                check.listo
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40"
                  : "bg-destructive/10 text-destructive ring-destructive/30",
              )}
            >
              {check.listo ? (
                <CheckCircle2 className="w-5 h-5" strokeWidth={1.7} />
              ) : (
                <XCircle className="w-5 h-5" strokeWidth={1.7} />
              )}
            </div>
            <div>
              <h3 className="font-heading text-lg leading-tight">
                {check.listo ? "Listo para publicar" : "Faltan datos"}
              </h3>
              <p className="text-xs text-muted-foreground">
                Verificaciones automáticas de los pasos 1–4.
              </p>
            </div>
          </header>

          <div className="flex flex-col gap-4">
            {gruposOrden.map((g) =>
              gruposMap[g] ? (
                <div key={g}>
                  <p className="overline text-muted-foreground mb-2">{g}</p>
                  <ul className="flex flex-col gap-1.5">
                    {gruposMap[g].map((c) => (
                      <li
                        key={c.id}
                        data-testid={EVENTO_PUB.check(c.id)}
                        data-ok={c.ok ? "true" : "false"}
                        className="flex items-start gap-3 rounded-lg border hairline bg-background px-3 py-2"
                      >
                        {c.ok ? (
                          <CheckCircle2
                            className="w-4 h-4 text-emerald-600 mt-0.5"
                            strokeWidth={2}
                          />
                        ) : (
                          <XCircle
                            className="w-4 h-4 text-destructive mt-0.5"
                            strokeWidth={2}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-tight">{c.label}</p>
                          {!c.ok && c.motivo && (
                            <p className="text-[11px] text-destructive mt-0.5">
                              {c.motivo}
                            </p>
                          )}
                        </div>
                        {!c.ok && (
                          <button
                            type="button"
                            data-testid={EVENTO_PUB.checkGoto(c.id)}
                            onClick={() => onGoToStep(c.paso)}
                            className="text-[11px] text-accent hover:underline shrink-0"
                          >
                            Ir a paso {c.paso}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )}
          </div>
        </section>

        {/* Resumen ejecutivo + Acciones */}
        <aside
          data-testid={EVENTO_PUB.resumen}
          className="flex flex-col gap-5 h-fit lg:sticky lg:top-24"
        >
          <section className="rounded-2xl border hairline bg-card overflow-hidden">
            {esUrlImagenSegura(valores.imagenPrincipal) ? (
              <img
                src={sanearUrlImagen(valores.imagenPrincipal)}
                alt={valores.nombre || "Evento"}
                className="w-full h-32 object-cover"
                onError={fallbackImagenEvento}
              />
            ) : (
              <div className="w-full h-32 bg-muted grid place-items-center text-muted-foreground text-xs">
                Sin imagen
              </div>
            )}
            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="overline text-accent">Resumen ejecutivo</span>
                <EstadoEventoBadge estado={valores.estado || ESTADO_EVENTO.BORRADOR} />
              </div>
              <h3 className="font-heading text-xl leading-tight">
                {valores.nombre || "Sin nombre"}
              </h3>
              <ul className="text-sm space-y-1.5">
                <ResumenLinea label="Fecha" value={`${formatearFechaCorta(valores.fecha) || "—"} · ${valores.hora || "—"}`} />
                <ResumenLinea
                  label="Modalidad"
                  value={esAG ? "Acceso General" : "Mesas"}
                />
                <ResumenLinea label="Salón" value={valores.salon || "—"} />
                <ResumenLinea
                  label="Tipo de acceso"
                  value={valores.tipoAcceso === "SOCIOS_INVITADOS" ? "Socios e invitados" : "Solo socios"}
                />
                <ResumenLinea label="Edad mínima" value={`${valores.edadMinima ?? "—"} años`} />
                <ResumenLinea label="Máx. por usuario" value={`${valores.limiteBoletosPorUsuario ?? "—"} boletos`} />
                <ResumenLinea
                  label="Ventana venta"
                  value={
                    valores.ventaInicioFecha && valores.ventaCierreFecha
                      ? `${formatearFechaCorta(valores.ventaInicioFecha)} → ${formatearFechaCorta(valores.ventaCierreFecha)}`
                      : "—"
                  }
                />
                {esAG ? (
                  <>
                    <ResumenLinea label="Salones activos" value={resumenAG.salonesActivos} />
                    <ResumenLinea
                      label="Capacidad vendible"
                      value={`${resumenAG.capacidadTotalActiva} lugares`}
                    />
                  </>
                ) : (
                  <>
                    <ResumenLinea label="Capacidad total" value={`${desglose.capacidad.total} lugares`} />
                    <ResumenLinea label="Disponibles" value={desglose.capacidad.DISPONIBLE} />
                    <ResumenLinea label="Reservadas" value={desglose.capacidad.RESERVADA} />
                    <ResumenLinea label="Protocolo" value={desglose.capacidad.PROTOCOLO} />
                    <ResumenLinea label="Bloqueadas" value={desglose.capacidad.BLOQUEADA} />
                    <ResumenLinea label="Total de mesas" value={(valores.mesas || []).length} />
                  </>
                )}
              </ul>
              <div className="border-t hairline pt-3">
                <p className="overline text-muted-foreground mb-2">
                  {esAG ? "Salones y precios" : "Precios"}
                </p>
                {esAG ? (
                  salonesAG.length === 0 ? (
                    <p className="text-xs text-muted-foreground">—</p>
                  ) : (
                    <ul className="text-sm space-y-1" data-testid="pub-ag-salones">
                      {salonesAG.map((s) => (
                        <li key={s.id} className="flex justify-between gap-3">
                          <span className="text-muted-foreground truncate">
                            {s.salon} {s.activo ? "" : "· inactivo"}
                          </span>
                          <span className="font-medium tabular-nums whitespace-nowrap">
                            {s.capacidad} · {fmtDinero(s.precioPorBoleto)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )
                ) : salones.length === 0 ? (
                  <p className="text-xs text-muted-foreground">—</p>
                ) : (
                  <ul className="text-sm space-y-1">
                    {salones.map((s) => (
                      <li key={s} className="flex justify-between">
                        <span className="text-muted-foreground truncate">{s}</span>
                        <span className="font-medium tabular-nums">
                          {fmtDinero(valores.precios?.[s])}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* Acciones */}
          <div className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              data-testid={EVENTO_PUB.btnPreview}
              onClick={() => setOpenPreview(true)}
              className="rounded-xl w-full"
            >
              <Eye className="w-4 h-4 mr-2" />
              Vista previa del evento
            </Button>
            <Button
              type="button"
              data-testid={EVENTO_PUB.btnPublicar}
              disabled={!check.listo || yaPublicado}
              onClick={() => setOpenConfirm(true)}
              className="rounded-xl w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Send className="w-4 h-4 mr-2" />
              {yaPublicado ? "Evento publicado" : "Publicar evento"}
            </Button>
            {!yaPublicado && (
              <p className="text-[11px] text-muted-foreground">
                Al publicar quedará como <b>{estadoObjetivo === "VENTA_ACTIVA" ? "Venta activa" : "Publicado"}</b> según la ventana de venta.
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Diálogo de vista previa (reutiliza EventoDetalleView del socio) */}
      <Dialog open={openPreview} onOpenChange={setOpenPreview}>
        <DialogContent data-testid={EVENTO_PUB.previewDialog} className="max-w-3xl max-h-[90vh] overflow-y-auto p-6">
          <VistaPreviaSocio evento={valores} onClose={() => setOpenPreview(false)} />
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación de publicación */}
      <Dialog open={openConfirm} onOpenChange={setOpenConfirm}>
        <DialogContent data-testid={EVENTO_PUB.confirmDialog}>
          <DialogHeader>
            <DialogTitle>Publicar evento</DialogTitle>
            <DialogDescription>
              ¿Desea publicar este evento? La información será visible para los socios.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border hairline bg-muted/30 p-4">
            <p className="text-sm font-medium mb-2">
              ¿Desea enviar una notificación a los socios sobre este evento?
            </p>
            <RadioGroup
              value={notificar}
              onValueChange={setNotificar}
              className="flex flex-col gap-2"
              data-testid={EVENTO_PUB.notifRadio}
            >
              <label className="flex items-center gap-3 rounded-lg border hairline bg-background px-3 py-2 cursor-pointer">
                <RadioGroupItem value="si" id="notif-si" data-testid={EVENTO_PUB.notifSi} />
                <Bell className="w-3.5 h-3.5 text-accent" strokeWidth={1.8} />
                <span className="text-sm">Sí, enviar cuando el servicio esté disponible</span>
              </label>
              <label className="flex items-center gap-3 rounded-lg border hairline bg-background px-3 py-2 cursor-pointer">
                <RadioGroupItem value="no" id="notif-no" data-testid={EVENTO_PUB.notifNo} />
                <BellOff className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.8} />
                <span className="text-sm">No, publicar sin notificar</span>
              </label>
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenConfirm(false)}>
              Cancelar
            </Button>
            <Button
              data-testid={EVENTO_PUB.btnConfirmarPublicar}
              onClick={publicar}
              className="bg-primary text-primary-foreground"
            >
              Confirmar publicación
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResumenLinea({ label, value }) {
  return (
    <li className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums truncate max-w-[60%] text-right">
        {value}
      </span>
    </li>
  );
}

function VistaPreviaSocio({ evento, onClose }) {
  const adap = useMemo(() => adaptarEventoParaSocio(evento), [evento]);
  if (!adap) return null;
  return (
    <div data-testid={EVENTO_PUB.previewCard} className="flex flex-col gap-4">
      <p className="text-[11px] uppercase tracking-widest text-accent">
        Vista previa · exactamente lo que verá el socio
      </p>
      <EventoDetalleView evento={adap} mode="preview" />
      <div className="flex justify-end pt-2 border-t hairline">
        <Button variant="outline" onClick={onClose} data-testid={EVENTO_PUB.previewClose}>
          Cerrar vista previa
        </Button>
      </div>
    </div>
  );
}
